import React from 'react';
import { ToggleButtonGroupStyled, ToggleButtonStyled } from './style';
import { BMRTitle, BMRValue } from '../malnutritionScreening/glim/style';
import { useDispatch } from 'react-redux';
import { setSummaryData } from '../../store/slices/metaDataSlice';
import { useAppSelector } from '../../store/hooks';
import { metaDataSelector } from '../../store/globalSelectors';

export const ToggleButtons = ({ name }: { name: string }) => {
  const [value, setValue] = React.useState('');
  const { summary } = useAppSelector(metaDataSelector);
  const dispatch = useDispatch();

  const handleAdultEnergyToggle = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string
  ) => {
    setValue(newValue);
    const clickedBtn = event.currentTarget;
    const title = clickedBtn?.querySelector('h2');
    const value = clickedBtn?.querySelector('p');
    dispatch(
      setSummaryData({
        ...summary,
        selectedAdultEnergy: {
          name: title?.innerHTML.replace(':', ''),
          value: value?.innerHTML
        }
      })
    );
  };
  const handlePaediatricEnergyToggle = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string
  ) => {
    setValue(newValue);
    const clickedBtn = event.currentTarget;
    const title = clickedBtn?.querySelector('h2');
    const value = clickedBtn?.querySelector('p');
    dispatch(
      setSummaryData({
        ...summary,
        selectedPaediatricEnergy: {
          name: title?.innerHTML.replace(':', ''),
          value: value?.innerHTML
        }
      })
    );
  };
  const handlePaediatricProteinToggle = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string
  ) => {
    setValue(newValue);
    const clickedBtn = event.currentTarget;
    const title = clickedBtn?.querySelector('h2');
    const value = clickedBtn?.querySelector('p');
    dispatch(
      setSummaryData({
        ...summary,
        selectedPaediatricProtein: {
          name: title?.innerHTML.replace(' (g/day):', ''),
          value: value?.innerHTML
        }
      })
    );
  };
  return (
    <>
      {name === 'ADULT_ENERGY' && (
        <ToggleButtonGroupStyled
          color="primary"
          orientation="vertical"
          value={summary.selectedAdultEnergy.name}
          exclusive
          onChange={handleAdultEnergyToggle}
        >
          <ToggleButtonStyled
            value="PENG 2018"
            aria-label="centered"
            disabled={summary.estimatingRequirements.peng2018 === 0}
            data-testid="peng2018"
          >
            <BMRTitle sx={{ textAlign: 'left', width: '50%' }} variant="h2">
              {'PENG 2018:'}
            </BMRTitle>
            <BMRValue>{summary.estimatingRequirements.peng2018}</BMRValue>
          </ToggleButtonStyled>
          <ToggleButtonStyled
            value="Henry Oxford"
            aria-label="centered"
            disabled={summary.estimatingRequirements.henryOxford === 0}
          >
            <BMRTitle
              sx={{
                textAlign: 'left',
                width: '75%',
                textTransform: 'capitalize'
              }}
              variant="h2"
            >
              {'Henry Oxford:'}
            </BMRTitle>
            <BMRValue>{summary.estimatingRequirements.henryOxford}</BMRValue>
          </ToggleButtonStyled>
          <ToggleButtonStyled
            value="Harris Benedict"
            aria-label="centered"
            disabled={summary.estimatingRequirements.harrisBenedict === 0}
          >
            <BMRTitle
              sx={{
                textAlign: 'left',
                width: '75%',
                textTransform: 'capitalize'
              }}
              variant="h2"
            >
              {'Harris Benedict:'}
            </BMRTitle>
            <BMRValue>{summary.estimatingRequirements.harrisBenedict}</BMRValue>
          </ToggleButtonStyled>
          <ToggleButtonStyled
            value="Simple Caloric Equation"
            aria-label="centered"
            disabled={
              summary.estimatingRequirements.simpleCaloricEquation.min === 0 &&
              summary.estimatingRequirements.simpleCaloricEquation.max === 0
            }
          >
            <BMRTitle
              sx={{
                textAlign: 'left',
                width: '50%',
                textTransform: 'capitalize'
              }}
              variant="h2"
            >
              {'Simple Caloric Equation:'}
            </BMRTitle>
            <BMRValue>
              {`${summary.estimatingRequirements.simpleCaloricEquation.min} - ${summary.estimatingRequirements.simpleCaloricEquation.max}`}
            </BMRValue>
          </ToggleButtonStyled>
          <ToggleButtonStyled
            value="PENN State"
            aria-label="centered"
            disabled={summary.estimatingRequirements.pennState === 0}
          >
            <BMRTitle
              sx={{
                textAlign: 'left',
                width: '75%',
                textTransform: 'capitalize'
              }}
              variant="h2"
            >
              {'PENN State:'}
            </BMRTitle>
            <BMRValue>{summary.estimatingRequirements.pennState}</BMRValue>
          </ToggleButtonStyled>
          <ToggleButtonStyled
            value="Modified PENN State"
            aria-label="centered"
            disabled={summary.estimatingRequirements.modifiedPennState === 0}
          >
            <BMRTitle
              sx={{
                textAlign: 'left',
                width: '50%',
                textTransform: 'capitalize'
              }}
              variant="h2"
            >
              {'Modified PENN State:'}
            </BMRTitle>
            <BMRValue>
              {summary.estimatingRequirements.modifiedPennState}
            </BMRValue>
          </ToggleButtonStyled>
          <ToggleButtonStyled
            value="Faisy"
            aria-label="centered"
            disabled={summary.estimatingRequirements.faisyEquation === 0}
          >
            <BMRTitle
              sx={{
                textAlign: 'left',
                width: '75%',
                textTransform: 'capitalize'
              }}
              variant="h2"
            >
              {'Faisy:'}
            </BMRTitle>
            <BMRValue>{summary.estimatingRequirements.faisyEquation}</BMRValue>
          </ToggleButtonStyled>
          <ToggleButtonStyled
            value="Mifflin-St.Jeor"
            aria-label="centered"
            disabled={summary.estimatingRequirements.mifflinStJeor === 0}
          >
            <BMRTitle
              sx={{
                textAlign: 'left',
                width: '75%',
                textTransform: 'capitalize'
              }}
              variant="h2"
            >
              {'Mifflin-St.Jeor:'}
            </BMRTitle>
            <BMRValue>{summary.estimatingRequirements.mifflinStJeor}</BMRValue>
          </ToggleButtonStyled>
        </ToggleButtonGroupStyled>
      )}

      {name === 'PAEDIATRIC_ENERGY' && (
        <ToggleButtonGroupStyled
          color="primary"
          orientation="vertical"
          value={summary.selectedPaediatricEnergy.name}
          exclusive
          onChange={handlePaediatricEnergyToggle}
        >
          <ToggleButtonStyled
            value="EFSA for Age"
            aria-label="centered"
            disabled={summary.paediatricEPF.energyForAge === 0}
            data-testid="efsaforage"
          >
            <BMRTitle
              sx={{
                textAlign: 'left',
                width: '75%',
                textTransform: 'none'
              }}
              variant="h2"
            >
              {'EFSA for Age:'}
            </BMRTitle>
            <BMRValue>{summary.paediatricEPF.energyForAge}</BMRValue>
          </ToggleButtonStyled>
          <ToggleButtonStyled
            value="EFSA for Weight"
            aria-label="centered"
            disabled={summary.paediatricEPF.energyForWeight === 0}
          >
            <BMRTitle
              sx={{
                textAlign: 'left',
                width: '75%',
                textTransform: 'none'
              }}
              variant="h2"
            >
              {'EFSA for Weight:'}
            </BMRTitle>
            <BMRValue>{summary.paediatricEPF.energyForWeight}</BMRValue>
          </ToggleButtonStyled>
        </ToggleButtonGroupStyled>
      )}

      {name === 'PAEDIATRIC_PROTEIN' && (
        <ToggleButtonGroupStyled
          color="primary"
          orientation="vertical"
          value={summary.selectedPaediatricProtein.name}
          exclusive
          onChange={handlePaediatricProteinToggle}
        >
          <ToggleButtonStyled
            value="AR"
            aria-label="centered"
            disabled={summary.paediatricEPF.energyForAge === 0}
            data-testid="ar"
          >
            <BMRTitle
              sx={{
                textAlign: 'left',
                width: '75%',
                textTransform: 'math-auto'
              }}
              variant="h2"
            >
              {'AR (g/day):'}
            </BMRTitle>
            <BMRValue>{summary.paediatricEPF.ar}</BMRValue>
          </ToggleButtonStyled>
          <ToggleButtonStyled
            value="PRI"
            aria-label="centered"
            disabled={summary.paediatricEPF.energyForWeight === 0}
          >
            <BMRTitle
              sx={{
                textAlign: 'left',
                width: '75%',
                textTransform: 'math-auto'
              }}
              variant="h2"
            >
              {'PRI (g/day):'}
            </BMRTitle>
            <BMRValue>{summary.paediatricEPF.pri}</BMRValue>
          </ToggleButtonStyled>
          <ToggleButtonStyled
            value="EAR"
            aria-label="centered"
            disabled={summary.paediatricEPF.energyForAge === 0}
          >
            <BMRTitle
              sx={{
                textAlign: 'left',
                width: '75%',
                textTransform: 'math-auto'
              }}
              variant="h2"
            >
              {'EAR (g/day):'}
            </BMRTitle>
            <BMRValue>{summary.paediatricEPF.ear}</BMRValue>
          </ToggleButtonStyled>
          <ToggleButtonStyled
            value="RNI"
            aria-label="centered"
            disabled={summary.paediatricEPF.energyForWeight === 0}
          >
            <BMRTitle
              sx={{
                textAlign: 'left',
                width: '75%',
                textTransform: 'math-auto'
              }}
              variant="h2"
            >
              {'RNI (g/day):'}
            </BMRTitle>
            <BMRValue>{summary.paediatricEPF.rni}</BMRValue>
          </ToggleButtonStyled>
        </ToggleButtonGroupStyled>
      )}
    </>
  );
};
